/**
 * The browser half of reCAPTCHA.
 *
 * Deliberately not a React hook at the top: the script is global, loads once
 * per page, and three different forms need a token from it. A module that
 * remembers what it already loaded is the honest shape for that; a hook would
 * either load it three times or need a provider nobody else wants.
 *
 * The site key comes from `/v1/meta`, which the login screen already reads —
 * so a deployment that has not configured reCAPTCHA sends no key, this never
 * loads a script, and every form behaves exactly as it did before.
 */

export type RecaptchaVersion = 'v2' | 'v3';
export type RecaptchaAction = 'register' | 'login' | 'forgotPassword' | 'demoRequest';

export interface RecaptchaConfig {
  enabled: boolean;
  siteKey: string;
  version: RecaptchaVersion;
  actions: RecaptchaAction[];
}

interface Grecaptcha {
  ready(cb: () => void): void;
  execute(siteKey: string, opts: { action: string }): Promise<string>;
  render(container: HTMLElement, opts: { sitekey: string; callback?: (t: string) => void }): number;
  getResponse(widgetId?: number): string;
  reset(widgetId?: number): void;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let scriptPromise: Promise<Grecaptcha | null> | null = null;

/**
 * Loads Google's script once and resolves when it is usable.
 *
 * Resolves to `null` rather than rejecting when the script cannot load — an
 * ad blocker, a captive network, a region that cannot reach Google. The form
 * then submits without a token, and the server decides: it fails open too, so
 * a blocked script is a degraded check rather than a person locked out of
 * their own account.
 */
function loadScript(cfg: RecaptchaConfig): Promise<Grecaptcha | null> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<Grecaptcha | null>((resolve) => {
    if (window.grecaptcha) return resolve(window.grecaptcha);
    const el = document.createElement('script');
    // v3 needs the key up front to mint tokens; v2 renders widgets instead.
    el.src =
      cfg.version === 'v3'
        ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(cfg.siteKey)}`
        : 'https://www.google.com/recaptcha/api.js?render=explicit';
    el.async = true;
    el.defer = true;
    el.onload = () => {
      const g = window.grecaptcha;
      if (!g) return resolve(null);
      g.ready(() => resolve(g));
    };
    el.onerror = () => resolve(null);
    document.head.appendChild(el);
  });
  return scriptPromise;
}

/** True when this deployment guards this particular form. */
export function guards(cfg: RecaptchaConfig | null, action: RecaptchaAction): boolean {
  return !!cfg?.enabled && !!cfg.siteKey && cfg.actions.includes(action);
}

/** Starts loading early, so the first submit does not wait for a network round trip. */
export function preload(cfg: RecaptchaConfig | null): void {
  if (cfg?.enabled && cfg.siteKey && cfg.version === 'v3') void loadScript(cfg);
}

/**
 * A token for one action, or undefined when there is nothing to send.
 *
 * Undefined is a real answer: reCAPTCHA off, this form not guarded, or the
 * script blocked. The server treats all three the same way.
 */
export async function token(
  cfg: RecaptchaConfig | null,
  action: RecaptchaAction,
): Promise<string | undefined> {
  if (!guards(cfg, action) || cfg!.version !== 'v3') return undefined;
  const g = await loadScript(cfg!);
  if (!g) return undefined;
  try {
    return await g.execute(cfg!.siteKey, { action });
  } catch {
    return undefined;
  }
}

/**
 * Renders the v2 checkbox into `container` and reports the token when ticked.
 * Returns a teardown, or null when v2 is not in play.
 */
export async function renderCheckbox(
  cfg: RecaptchaConfig | null,
  action: RecaptchaAction,
  container: HTMLElement,
  onToken: (t: string) => void,
): Promise<(() => void) | null> {
  if (!guards(cfg, action) || cfg!.version !== 'v2') return null;
  const g = await loadScript(cfg!);
  if (!g) return null;
  // A second render into the same node throws; the caller's effect cleanup
  // empties it, which is what makes a re-render safe.
  container.innerHTML = '';
  const id = g.render(container, { sitekey: cfg!.siteKey, callback: onToken });
  return () => {
    try {
      g.reset(id);
    } catch {
      /* the widget is already gone */
    }
  };
}
